import { useState, useEffect } from "react";
import styled from "@emotion/styled/macro";
import { Tooltip } from "@sunrun/experience-ui-components";
import plusIcon from "../../assets/images/plus.svg";
import minusIcon from "../../assets/images/minus.svg";
import { EditOfferProduct } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { ProductVariantOptions } from "./ProductVariantOptions";
import { ProductTitleWithInfoModal } from "components/atoms/ProductTitleWithInfoModal";
import { AddPill } from "components/atoms/AddPill";
import { useDebounce } from "hooks/useDebounce";
import { ProductImage } from "components/atoms/ProductImage";

type ProductAccessoryProps = {
  productId: string;
  title: string;
  imageUrl: string;
  added: boolean;
  disabled: boolean;
  quantity?: number;
  onAccessoryChange: (
    id: string,
    quantity: number,
    type: "delete" | "add" | "update"
  ) => void;
  selectedVariantId?: string;
  variants?: EditOfferProduct["variants"];
  onVariantChange: (productId: string, variantId: string) => void;
  required?: boolean;
  minQuantity?: number | null;
  maxQuantity?: number | null;
};

// Required accessories must be present at >= 1; optional accessories with no
// declared min should also start at 1 when added.
const resolveAddedQuantity = (minQuantity?: number | null) =>
  Math.max(minQuantity ?? 1, 1);

const REQUIRED_ACCESSORY_TOOLTIP = "Required accessory in this jurisdiction";

const ProductAccessory = ({
  productId,
  title,
  imageUrl,
  added,
  quantity = 0,
  disabled,
  onAccessoryChange,
  selectedVariantId,
  variants = [],
  onVariantChange,
  required = false,
  minQuantity = null,
  maxQuantity = null,
}: ProductAccessoryProps) => {
  const lowerBound = resolveAddedQuantity(minQuantity);
  const upperBound = maxQuantity ?? Number.POSITIVE_INFINITY;

  const initialAdded = required ? true : added;
  const initialQuantity = required
    ? Math.max(quantity, lowerBound)
    : added
    ? Math.max(quantity, lowerBound)
    : 0;

  const [itemAdded, setItemAdded] = useState(initialAdded);
  const [itemQuantity, setItemQuantity] = useState(initialQuantity);

  // Wait for 1s for the user to stop making actions to update "debouncedValues"
  const debouncedValues = useDebounce(`${itemAdded}${itemQuantity}`, 1000);

  useEffect(() => {
    if (disabled) {
      return;
    }
    // Only want to update the parent component when the user has stopped making actions
    // and if result is different from the initial values
    if (itemAdded !== added || itemQuantity !== quantity) {
      const type = itemAdded
        ? itemAdded === added
          ? "update"
          : "add"
        : "delete";
      onAccessoryChange(productId, itemQuantity, type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    added,
    debouncedValues,
    disabled,
    productId,
    onAccessoryChange,
    quantity,
  ]);

  const toggleAdded = () => {
    if (required) {
      // Required accessories cannot be removed.
      return;
    }
    setItemAdded((prev) => {
      const newValue = !prev;
      setItemQuantity(newValue ? lowerBound : 0);
      return newValue;
    });
  };

  // Hide the minus/plus icons when we're already at the corresponding bound
  // so the UI doesn't show non-actionable affordances. `disabled` (loading
  // state) keeps the icons visible but greys them out so the user can still
  // see what controls exist.
  const showMinus = itemQuantity > lowerBound;
  const showPlus = itemQuantity < upperBound;

  const handleMinus = () => {
    if (disabled || itemQuantity <= lowerBound) {
      return;
    }
    setItemQuantity((prev) => Math.max(prev - 1, lowerBound));
  };

  const handlePlus = () => {
    if (disabled || itemQuantity >= upperBound) {
      return;
    }
    setItemQuantity((prev) => Math.min(prev + 1, upperBound));
  };

  const addPill = (
    <AddPill
      itemAdded={itemAdded}
      toggleAdded={toggleAdded}
      disabled={disabled}
      locked={required}
      ariaLabel={required ? REQUIRED_ACCESSORY_TOOLTIP : undefined}
    />
  );

  return (
    <ProductAccessoryContainer>
      <ProductImage imageUrl={imageUrl}>
        {required ? (
          <RequiredAccessoryTooltip
            content={REQUIRED_ACCESSORY_TOOLTIP}
            direction="bottom"
          >
            <RequiredAccessoryTooltipTarget data-testid="required-accessory-tooltip">
              {addPill}
            </RequiredAccessoryTooltipTarget>
          </RequiredAccessoryTooltip>
        ) : (
          addPill
        )}
      </ProductImage>
      <div>
        <AccessoryInfoRow>
          <ProductTitleWithInfoModal title={title} />
          {itemAdded && (
            <QuantitySection $disabled={disabled}>
              {showMinus && (
                <MinusIcon
                  $disabled={disabled}
                  onClick={disabled ? undefined : handleMinus}
                  data-testid="minus"
                />
              )}
              <QuantityCounter data-testid="quantity">
                {itemQuantity}
              </QuantityCounter>
              {showPlus && (
                <PlusIcon
                  $disabled={disabled}
                  onClick={disabled ? undefined : handlePlus}
                  data-testid="plus"
                />
              )}
            </QuantitySection>
          )}
        </AccessoryInfoRow>
        <ProductVariantOptions
          productId={productId}
          disabled={!itemAdded || disabled}
          selectedVariantId={selectedVariantId}
          variants={variants}
          onVariantChange={onVariantChange}
        ></ProductVariantOptions>
      </div>
    </ProductAccessoryContainer>
  );
};

const ProductAccessoryContainer = styled.div`
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 48px;
  @media (max-width: 536px) {
    width: 100%;
    padding-bottom: 40px;
  }
`;

const RequiredAccessoryTooltip = styled(Tooltip)`
  align-self: flex-start;

  && .sr-tooltip-content,
  && .sr-tooltip-content-mobile {
    box-sizing: border-box;
    max-width: min(360px, calc(100vw - 48px));
    line-height: 24px;
    white-space: normal;
  }
`;

const RequiredAccessoryTooltipTarget = styled.span`
  display: inline-flex;
  cursor: not-allowed;
`;

const AccessoryInfoRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) max-content;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;

  > div:first-of-type {
    flex: 1 1 0;
    min-width: 0;

    h6 {
      margin: 0;
      overflow-wrap: anywhere;
    }
  }
`;
const QuantitySection = styled.div<{ $disabled: boolean }>`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  ${(props) =>
    props.$disabled &&
    `
    cursor: progress;
    & > div {
      cursor: progress;
    }  
  `}
`;
const QuantityCounter = styled.div`
  min-width: 18px;
  text-align: center;
  user-select: none;
`;
// Hit area is intentionally larger than the visual glyph (10px) to give a
// comfortable touch/click target while keeping the rendered icon size.
const QuantityIconButton = styled.div<{ $disabled?: boolean }>`
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.4 : 1)};
  height: 32px;
  width: 32px;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 10px 10px;
  user-select: none;
`;
const MinusIcon = styled(QuantityIconButton)`
  background-image: url(${minusIcon});
`;
const PlusIcon = styled(QuantityIconButton)`
  background-image: url(${plusIcon});
`;

export type { ProductAccessoryProps };
export { ProductAccessory };
